import { LeaderboardAuthSession } from './leaderboard_auth_session.js';
import { LeaderboardAuthService } from './leaderboard_auth_service.js';

import { GlobalSettings } from '../../global_settings.js';

const getBase = (): string => GlobalSettings.LEADERBOARD_BASE_URL;
const getLeaderboardUrl = (): string => `${getBase()}/leaderboards`;
const getUsersUrl = (): string => `${getBase()}/users`;

const DEFAULT_TIMEOUT_MS = 10_000;

class LeaderboardApiError extends Error
{
  constructor(
    public readonly status: number,
    message: string,
  )
  {
    super( message );
    this.name = 'LeaderboardApiError';
  }
}

function buildUrl( path: string ): string
{
  const base = getLeaderboardUrl().replace( /\/+$/, '' );
  const trimmed = path.replace( /^\/+/, '' );
  return `${base}/${trimmed}`;
}

function buildHeaders(): Record<string, string>
{
  const token = LeaderboardAuthSession.getToken();
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  if ( token ) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

function isNativePlatform(): boolean
{
  try
  {
    const platform = ( globalThis as any )?.Capacitor?.getPlatform?.();
    return platform === 'android' || platform === 'ios';
  }
  catch
  {
    return false;
  }
}

async function doPostNative( url: string, body: unknown ): Promise<{ status: number; data: unknown }>
{
  const http: any = ( globalThis as any )?.Capacitor?.Plugins?.CapacitorHttp;
  if ( typeof http?.request !== 'function' ) throw new Error( 'CapacitorHttp not available' );

  return http.request( {
    url,
    method: 'POST',
    headers: buildHeaders(),
    data: body,
    connectTimeout: DEFAULT_TIMEOUT_MS,
    readTimeout: DEFAULT_TIMEOUT_MS,
  } );
}

async function doPostFetch( url: string, body: unknown ): Promise<{ status: number; data: unknown }>
{
  const controller = new AbortController();
  const timer = setTimeout( () => controller.abort(), DEFAULT_TIMEOUT_MS );
  try
  {
    const res = await fetch( url, {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify( body ),
      signal: controller.signal,
    } );
    const data = await res.json().catch( () => null );
    return { status: res.status, data };
  }
  catch ( e )
  {
    throw new Error( `[LeaderboardHttp] Network error: ${e}` );
  }
  finally
  {
    clearTimeout( timer );
  }
}

async function doPost( url: string, body: unknown ): Promise<{ status: number; data: unknown }>
{
  if ( isNativePlatform() )
  {
    try
    {
      return await doPostNative( url, body );
    }
    catch ( e )
    {
      console.warn( '[LeaderboardHttp] CapacitorHttp failed, falling back to fetch:', String( e ) );
    }
  }
  return doPostFetch( url, body );
}

// Responses are logged as one short line: LogOverlay keeps every console.log for the whole
// session (with the object and its JSON, on prod too), and a 1000-player board is ~250 KB per
// request. The full payload is printed on demand by `leadersDebug.dump()`.
export function logLeaders( source: string, data: unknown, started_at?: number ): void
{
  const took = started_at !== undefined ? ` ${Math.round( performance.now() - started_at )}ms` : '';
  console.log( `[Leaders] ${source}: ${summarizeLeaders( data )}${took}` );
}

function summarizeLeaders( data: unknown ): string
{
  if ( data === null || typeof data !== 'object' )
    return String( data );
  const users = ( data as { users?: unknown } ).users;
  if ( Array.isArray( users ) )
    return `users=${users.length}`;
  const json = JSON.stringify( data );
  return json.length <= 200 ? json : `${Math.round( json.length / 1024 )} KB`;
}

export async function leaderboardUsersPost<T>( path: string, body?: unknown ): Promise<T>
{
  const token = LeaderboardAuthSession.getToken();
  const base = getUsersUrl().replace( /\/+$/, '' );
  const url = `${base}/${path.replace( /^\/+/, '' )}`;

  console.log( `[LeaderboardHttp] → POST (users) ${path}`, body, `| token: ${token ? token.slice( 0, 20 ) + '...' : 'null'}` );

  const started_at = performance.now();
  let res = await doPost( url, body );

  if ( res.status === 401 )
  {
    console.warn( '[LeaderboardHttp] 401 — token expired, re-logging in' );
    LeaderboardAuthService.resetSession();
    await LeaderboardAuthService.login();
    res = await doPost( url, body );
  }

  if ( res.status < 200 || res.status >= 300 )
  {
    console.warn( `[LeaderboardHttp] ← ${res.status} POST (users) ${path}`, res.data );
    throw new LeaderboardApiError( res.status, `HTTP ${res.status}` );
  }

  logLeaders( `← ${res.status} users/${path.replace( /^\/+/, '' )}`, res.data, started_at );
  return res.data as T;
}

export async function leaderboardPost<T>( path: string, body?: unknown ): Promise<T>
{
  const token = LeaderboardAuthSession.getToken();
  console.log( `[LeaderboardHttp] → POST ${path}`, body, `| token: ${token ? token.slice( 0, 20 ) + '...' : 'null'}` );

  const url = buildUrl( path );
  const started_at = performance.now();
  let res = await doPost( url, body );

  if ( res.status === 401 )
  {
    console.warn( '[LeaderboardHttp] 401 — token expired, re-logging in' );
    LeaderboardAuthService.resetSession();
    await LeaderboardAuthService.login();
    res = await doPost( url, body );
  }

  if ( res.status < 200 || res.status >= 300 )
  {
    console.warn( `[LeaderboardHttp] ← ${res.status} POST ${path}`, res.data );
    throw new LeaderboardApiError( res.status, `HTTP ${res.status}` );
  }

  logLeaders( `← ${res.status} leaderboards/${path.replace( /^\/+/, '' )}`, res.data, started_at );
  return res.data as T;
}
